import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  AuthError
} from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { AccessPermission, hasAccessPermission } from '../types/adminAccess';

export interface AdminProfile {
  uid: string;
  adminDocId?: string;
  email: string;
  displayName: string;
  role: 'superAdmin' | 'admin' | 'employee';
  access: AccessPermission[];
  regions: string[];
  active: boolean;
  inviteStatus?: 'pending' | 'active';
}

interface AuthContextType {
  currentUser: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  adminProfile: AdminProfile | null;
  hasAccess: (permission: AccessPermission) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const mapAdminProfile = (user: User, adminData: any, adminDocId?: string): AdminProfile => ({
  uid: user.uid,
  adminDocId,
  email: adminData.email || user.email || '',
  displayName: adminData.displayName || adminData.display_name || user.displayName || user.email || 'Administrator',
  role: adminData.role || 'admin',
  access: Array.isArray(adminData.access) ? adminData.access : [],
  regions: Array.isArray(adminData.regions) ? adminData.regions : [],
  active: adminData.active === true,
  inviteStatus: adminData.inviteStatus || 'active'
});

const normalizeEmail = (value?: string | null) => (value || '').trim().toLowerCase();

const resolveAdminProfile = async (user: User): Promise<{ data: any; docId: string } | null> => {
  if (!db) {
    return null;
  }

  const directAdminDoc = await getDoc(doc(db, 'admins', user.uid));
  if (directAdminDoc.exists()) {
    return {
      data: directAdminDoc.data(),
      docId: directAdminDoc.id
    };
  }

  const fallbackSnapshot = await getDocs(
    query(collection(db, 'admins'), where('uid', '==', user.uid), limit(1))
  );

  if (!fallbackSnapshot.empty) {
    return {
      data: fallbackSnapshot.docs[0].data(),
      docId: fallbackSnapshot.docs[0].id
    };
  }

  const email = normalizeEmail(user.email);
  if (email) {
    // Legacy org records may be keyed incorrectly but still carry matching email.
    const emailSnapshot = await getDocs(
      query(collection(db, 'admins'), where('email', '==', email), limit(1))
    );

    if (!emailSnapshot.empty) {
      return {
        data: emailSnapshot.docs[0].data(),
        docId: emailSnapshot.docs[0].id
      };
    }
  }

  return null;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [adminProfile, setAdminProfile] = useState<AdminProfile | null>(null);

  const clearAdminState = useCallback(() => {
    setIsAdmin(false);
    setIsSuperAdmin(false);
    setAdminProfile(null);
  }, []);

  const checkAdminStatus = useCallback(async (user: User | null) => {
    if (!user || !user.uid || !db) {
      clearAdminState();
      return;
    }

    try {
      const resolvedAdmin = await resolveAdminProfile(user);

      if (!resolvedAdmin) {
        clearAdminState();
        return;
      }
      const adminData = resolvedAdmin.data;
      const active = adminData.active === true;

      setIsAdmin(active);
      setIsSuperAdmin(active && adminData.role === 'superAdmin');
      setAdminProfile(mapAdminProfile(user, adminData, resolvedAdmin.docId));
    } catch (error) {
      console.error('Error checking admin status:', error);
      clearAdminState();
    }
  }, [clearAdminState]);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      if (!auth || !db) {
        throw new Error('Firebase Authentication not configured. Please check your Firebase settings.');
      }

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (!user.email) {
        await signOut(auth);
        throw new Error('User email not found.');
      }

      try {
        const resolvedAdmin = await resolveAdminProfile(user);

        if (!resolvedAdmin) {
          await signOut(auth);
          throw new Error(
            `Access denied. No admin profile found for ${normalizeEmail(user.email)} (uid: ${user.uid}).`
          );
        }
        const adminData = resolvedAdmin.data;

        if (adminData.active !== true) {
          await signOut(auth);
          throw new Error('Access denied. Your admin account is inactive.');
        }

        setCurrentUser(user);
        setIsAdmin(true);
        setIsSuperAdmin(adminData.role === 'superAdmin');
        setAdminProfile(mapAdminProfile(user, adminData, resolvedAdmin.docId));
      } catch (firestoreError: any) {
        if (firestoreError.message.includes('Access denied') || firestoreError.message.includes('not authorized')) {
          throw firestoreError;
        }

        if (firestoreError.code === 'permission-denied' || firestoreError.message.includes('Missing or insufficient permissions')) {
          await signOut(auth);
          throw new Error('Admin verification failed: Firestore access denied. Please check security rules allow admin reads.');
        }

        await signOut(auth);
        throw new Error(`Failed to verify admin status: ${firestoreError.message}`);
      }
    } catch (error) {
      const authError = error as any;
      console.error('Login error:', authError.message);
      throw new Error(authError.message);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      if (!auth) {
        console.warn('Firebase Authentication not configured');
        return;
      }

      await signOut(auth);
      setCurrentUser(null);
      clearAdminState();
    } catch (error) {
      const authError = error as AuthError;
      console.error('Logout error:', authError.message);
      throw new Error(authError.message);
    }
  };

  useEffect(() => {
    if (!auth) {
      console.warn('Firebase Auth not initialized');
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      await checkAdminStatus(user);
      setLoading(false);
    });

    return unsubscribe;
  }, [checkAdminStatus]);

  const value: AuthContextType = {
    currentUser,
    login,
    logout,
    loading,
    isAdmin,
    isSuperAdmin,
    adminProfile,
    hasAccess: (permission: AccessPermission) => hasAccessPermission(adminProfile, permission)
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
